/**
 * SchoolCMS - Admin CMS editor for school public website
 * Includes: About/Mission/Vision editor, Logo upload, Gallery, Contact info, Custom sections
 */
import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Upload, X, Plus, ExternalLink, Image, Globe, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

export const SchoolCMS = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const heroInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const { data: cms, isLoading } = useQuery({
    queryKey: ['school-cms', tenantId],
    queryFn: () => apiFetch(`/tenants/${tenantId}/cms`),
    enabled: !!tenantId,
  });

  const { data: tenant } = useQuery({
    queryKey: ['school-tenant', tenantId],
    queryFn: () => apiFetch(`/tenants/${tenantId}`),
    enabled: !!tenantId,
  });

  const { data: programmeData } = useQuery({
    queryKey: ['school-cms-programmes', tenantId],
    queryFn: () => apiFetch(`/tenants/${tenantId}/programmes`),
    enabled: !!tenantId,
    retry: false,
  });
  const programmes = programmeData?.items || [];

  const [form, setForm] = useState(null);

  React.useEffect(() => {
    if (cms && !form) {
      setForm({
        about_us: cms.about_us || '',
        mission: cms.mission || '',
        vision: cms.vision || '',
        contact_email: cms.contact_email || '',
        contact_phone: cms.contact_phone || '',
        address: cms.address || '',
        website_url: cms.website_url || '',
        social_facebook: cms.social_facebook || '',
        social_twitter: cms.social_twitter || '',
        social_instagram: cms.social_instagram || '',
        social_youtube: cms.social_youtube || '',
        social_linkedin: cms.social_linkedin || '',
        gallery_urls: cms.gallery_urls || [],
        custom_sections: cms.custom_sections || [],
        hero_title: cms.hero_title || '',
        hero_tagline: cms.hero_tagline || '',
        hero_cta_label: cms.hero_cta_label || '',
        school_type_key: cms.school_type_key || '',
        established_year: cms.established_year || '',
        stat_overrides: cms.stat_overrides || {},
        facilities: cms.facilities || [],
        facilities_note: cms.facilities_note || '',
        admissions_note: cms.admissions_note || '',
        apply_steps: cms.apply_steps || [],
        testimonials: cms.testimonials || [],
        hidden_programme_ids: cms.hidden_programme_ids || [],
        extra_programmes: cms.extra_programmes || [],
      });
    }
  }, [cms, form]);

  const saveMutation = useMutation({
    mutationFn: (data) => apiFetch(`/tenants/${tenantId}/cms`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast.success('CMS content saved');
      queryClient.invalidateQueries({ queryKey: ['school-cms', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      toast.success('Logo uploaded');
      queryClient.invalidateQueries({ queryKey: ['school-tenant', tenantId] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/upload-gallery`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      setForm(prev => ({ ...prev, gallery_urls: [...(prev.gallery_urls || []), data.url] }));
      toast.success('Image added to gallery');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (idx) => {
    setForm(prev => ({
      ...prev,
      gallery_urls: prev.gallery_urls.filter((_, i) => i !== idx),
    }));
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/upload-hero`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      toast.success('Hero image uploaded');
      queryClient.invalidateQueries({ queryKey: ['school-cms', tenantId] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const addCustomSection = () => {
    setForm(prev => ({
      ...prev,
      custom_sections: [...(prev.custom_sections || []), { title: '', body: '' }],
    }));
  };

  const updateCustomSection = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      custom_sections: prev.custom_sections.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const removeCustomSection = (idx) => {
    setForm(prev => ({
      ...prev,
      custom_sections: prev.custom_sections.filter((_, i) => i !== idx),
    }));
  };

  if (isLoading || !form) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  }

  const logoUrl = tenant?.logo_url
    ? (tenant.logo_url.startsWith('http') ? tenant.logo_url : `${API_URL}${tenant.logo_url}`)
    : null;

  const publicUrl = `${window.location.origin}/school/${tenantId}/website`;
  const applyUrl = `${window.location.origin}/school/${tenantId}/apply`;

  return (
    <div className="space-y-6" data-testid="school-cms-panel">
      {/* Quick Links */}
      <div className="flex flex-wrap gap-3">
        <a href={publicUrl} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium">
          <Globe className="h-4 w-4" /> View Public Website <ExternalLink className="h-3 w-3" />
        </a>
        <a href={applyUrl} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-800 font-medium">
          <ExternalLink className="h-4 w-4" /> View Application Page <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Logo Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">School Logo</CardTitle>
          <CardDescription>Upload your school's logo (PNG, JPG, SVG — max 5MB)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Image className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <div>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                     onChange={handleLogoUpload} data-testid="logo-file-input" />
              <Button variant="outline" onClick={() => logoInputRef.current?.click()}
                      disabled={uploading} data-testid="upload-logo-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {logoUrl ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-slate-400 mt-1">Works from any device — mobile, tablet, desktop</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hero / Banner Image Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Hero / Banner Image</CardTitle>
          <CardDescription>Upload a banner image for the school website hero section (JPEG, PNG, WebP — max 10MB)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="w-48 h-24 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
              {cms?.hero_image_url ? (
                <img src={cms.hero_image_url.startsWith('http') ? cms.hero_image_url : `${API_URL}${cms.hero_image_url}`} alt="Hero" className="w-full h-full object-cover" />
              ) : (
                <Image className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <div>
              <input ref={heroInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                     onChange={handleHeroUpload} data-testid="hero-file-input" />
              <Button variant="outline" onClick={() => heroInputRef.current?.click()}
                      disabled={uploading} data-testid="upload-hero-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {cms?.hero_image_url ? 'Change Hero Image' : 'Upload Hero Image'}
              </Button>
              <p className="text-xs text-slate-400 mt-1">Recommended: 1920x600 or wider</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About / Mission / Vision */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">About the School</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>About Us</Label>
            <textarea className="w-full border rounded-lg p-3 text-sm min-h-[100px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={form.about_us} onChange={e => setForm({ ...form, about_us: e.target.value })}
                      placeholder="Tell visitors about your school..." data-testid="cms-about-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Mission</Label>
              <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={form.mission} onChange={e => setForm({ ...form, mission: e.target.value })}
                        placeholder="Our mission..." data-testid="cms-mission-input" />
            </div>
            <div>
              <Label>Vision</Label>
              <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={form.vision} onChange={e => setForm({ ...form, vision: e.target.value })}
                        placeholder="Our vision..." data-testid="cms-vision-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact Information</CardTitle>
          <CardDescription>Displayed on the public website</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Contact Email</Label>
              <Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                     placeholder="info@school.edu" data-testid="cms-email-input" />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                     placeholder="+263 77 123 4567" data-testid="cms-phone-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                     placeholder="123 School Road, City" data-testid="cms-address-input" />
            </div>
            <div>
              <Label>Website URL</Label>
              <Input value={form.website_url} onChange={e => setForm({ ...form, website_url: e.target.value })}
                     placeholder="https://www.school.edu" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Social Media</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Facebook</Label>
              <Input value={form.social_facebook} onChange={e => setForm({ ...form, social_facebook: e.target.value })}
                     placeholder="https://facebook.com/school" />
            </div>
            <div>
              <Label>Twitter / X</Label>
              <Input value={form.social_twitter} onChange={e => setForm({ ...form, social_twitter: e.target.value })}
                     placeholder="https://twitter.com/school" />
            </div>
            <div>
              <Label>Instagram</Label>
              <Input value={form.social_instagram} data-testid="cms-instagram-input"
                     onChange={e => setForm({ ...form, social_instagram: e.target.value })}
                     placeholder="https://instagram.com/school" />
            </div>
            <div>
              <Label>YouTube</Label>
              <Input value={form.social_youtube} data-testid="cms-youtube-input"
                     onChange={e => setForm({ ...form, social_youtube: e.target.value })}
                     placeholder="https://youtube.com/@school" />
            </div>
            <div>
              <Label>LinkedIn</Label>
              <Input value={form.social_linkedin} data-testid="cms-linkedin-input"
                     onChange={e => setForm({ ...form, social_linkedin: e.target.value })}
                     placeholder="https://linkedin.com/company/school" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gallery */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Photo Gallery</CardTitle>
          <CardDescription>Upload school photos (max 10MB each)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
            {(form.gallery_urls || []).map((url, idx) => (
              <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border bg-slate-50">
                <img src={url.startsWith('http') ? url : `${API_URL}${url}`}
                     alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                <button onClick={() => removeGalleryImage(idx)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-gallery-${idx}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <div className="aspect-square rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                 onClick={() => galleryInputRef.current?.click()}>
              {uploading ? <Loader2 className="h-6 w-6 animate-spin text-indigo-400" /> : <Plus className="h-6 w-6 text-slate-400" />}
            </div>
          </div>
          <input ref={galleryInputRef} type="file" accept="image/*" className="hidden"
                 onChange={handleGalleryUpload} data-testid="gallery-file-input" />
        </CardContent>
      </Card>

      {/* Custom Sections */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Custom Sections</CardTitle>
              <CardDescription>Add extra content blocks to your website</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addCustomSection} data-testid="add-custom-section-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Section
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(form.custom_sections || []).map((section, idx) => (
            <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
              <button onClick={() => removeCustomSection(idx)}
                      className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                      data-testid={`remove-section-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </button>
              <div>
                <Label>Section Title</Label>
                <Input value={section.title} onChange={e => updateCustomSection(idx, 'title', e.target.value)}
                       placeholder="e.g. Extracurricular Activities" />
              </div>
              <div>
                <Label>Content</Label>
                <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          value={section.body} onChange={e => updateCustomSection(idx, 'body', e.target.value)}
                          placeholder="Section content..." />
              </div>
            </div>
          ))}
          {(form.custom_sections || []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No custom sections yet</p>
          )}
        </CardContent>
      </Card>

      {/* Hero copy & identity */}
      <Card data-testid="cms-hero-card">
        <CardHeader>
          <CardTitle className="text-base">Hero & identity</CardTitle>
          <CardDescription>What visitors read first, and the badge that says what kind of school this is</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Headline</Label>
              <Input value={form.hero_title} data-testid="cms-hero-title-input"
                     onChange={e => setForm({ ...form, hero_title: e.target.value })}
                     placeholder="left blank uses the school name" />
            </div>
            <div>
              <Label>Apply button label</Label>
              <Input value={form.hero_cta_label} data-testid="cms-cta-label-input"
                     onChange={e => setForm({ ...form, hero_cta_label: e.target.value })}
                     placeholder="Apply Now" />
            </div>
            <div className="md:col-span-2">
              <Label>Tagline</Label>
              <Input value={form.hero_tagline} data-testid="cms-hero-tagline-input"
                     onChange={e => setForm({ ...form, hero_tagline: e.target.value })}
                     placeholder="One sentence about the school" />
            </div>
            <div>
              <Label>School type</Label>
              <select value={form.school_type_key} data-testid="cms-school-type-select"
                      onChange={e => setForm({ ...form, school_type_key: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">— use the school record —</option>
                {(cms?.school_type_keys || []).map(k => (
                  <option key={k} value={k}>{k.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Established (year)</Label>
              <Input value={form.established_year} data-testid="cms-established-input"
                     onChange={e => setForm({ ...form, established_year: e.target.value })}
                     placeholder="e.g. 1998" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key stats */}
      <Card data-testid="cms-stats-card">
        <CardHeader>
          <CardTitle className="text-base">Key stats</CardTitle>
          <CardDescription>
            Left blank, these count themselves from live records. Type a figure to override it.
            Graduation rate is always typed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[['students', 'Students'], ['teachers', 'Academic staff'], ['programmes', 'Programmes'],
              ['graduation_rate', 'Graduation rate (e.g. 96%)'], ['years', 'Years of service']]
              .map(([k, label]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  <Input value={form.stat_overrides?.[k] || ''} data-testid={`cms-stat-${k}-input`}
                         onChange={e => setForm({
                           ...form,
                           stat_overrides: { ...form.stat_overrides, [k]: e.target.value },
                         })}
                         placeholder={k === 'graduation_rate' ? 'e.g. 96%' : 'live figure'} />
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Facilities */}
      <Card data-testid="cms-facilities-card">
        <CardHeader>
          <CardTitle className="text-base">Facilities</CardTitle>
          <CardDescription>Tick what this campus actually has — only ticked items are shown</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(cms?.facility_keys || []).map(k => {
              const on = (form.facilities || []).includes(k);
              return (
                <label key={k} data-testid={`cms-facility-${k}`}
                       className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                         on ? 'border-indigo-300 bg-indigo-50 text-indigo-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  <input type="checkbox" checked={on}
                         onChange={() => setForm({
                           ...form,
                           facilities: on ? form.facilities.filter(f => f !== k)
                                          : [...(form.facilities || []), k],
                         })} />
                  <span className="capitalize">{k.replace('_', ' ')}</span>
                </label>
              );
            })}
          </div>
          <div>
            <Label>Note under the heading</Label>
            <Input value={form.facilities_note} data-testid="cms-facilities-note-input"
                   onChange={e => setForm({ ...form, facilities_note: e.target.value })}
                   placeholder="Optional line about the campus" />
          </div>
        </CardContent>
      </Card>

      {/* Admissions band */}
      <Card data-testid="cms-admissions-card">
        <CardHeader>
          <CardTitle className="text-base">Admissions band</CardTitle>
          <CardDescription>Open intakes come from Admissions automatically — this is the wording around them</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Intro paragraph</Label>
            <textarea className="w-full border rounded-lg p-3 text-sm min-h-[70px]"
                      value={form.admissions_note} data-testid="cms-admissions-note-input"
                      onChange={e => setForm({ ...form, admissions_note: e.target.value })}
                      placeholder="Who you are looking for and when applications close..." />
          </div>
          <div>
            <Label>Steps to apply</Label>
            <div className="space-y-2 mt-1">
              {(form.apply_steps?.length ? form.apply_steps : (cms?.default_apply_steps || []))
                .map((s, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input value={s} data-testid={`cms-apply-step-${idx}`}
                           onChange={e => {
                             const base = form.apply_steps?.length ? form.apply_steps : (cms?.default_apply_steps || []);
                             setForm({ ...form, apply_steps: base.map((x, i) => i === idx ? e.target.value : x) });
                           }} />
                    <Button variant="ghost" size="icon" data-testid={`cms-remove-step-${idx}`}
                            onClick={() => {
                              const base = form.apply_steps?.length ? form.apply_steps : (cms?.default_apply_steps || []);
                              setForm({ ...form, apply_steps: base.filter((_, i) => i !== idx) });
                            }}>
                      <Trash2 className="h-4 w-4 text-slate-400" />
                    </Button>
                  </div>
                ))}
              <Button variant="outline" size="sm" data-testid="cms-add-step-btn"
                      onClick={() => {
                        const base = form.apply_steps?.length ? form.apply_steps : (cms?.default_apply_steps || []);
                        setForm({ ...form, apply_steps: [...base, ''] });
                      }}>
                <Plus className="h-4 w-4 mr-1" /> Add step
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Programmes on the public page */}
      {programmes.length > 0 && (
        <Card data-testid="cms-programmes-card">
          <CardHeader>
            <CardTitle className="text-base">Programmes shown publicly</CardTitle>
            <CardDescription>Every active programme is listed — untick one to hide it from the public page</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {programmes.map(p => {
                const hidden = (form.hidden_programme_ids || []).includes(p.id);
                return (
                  <label key={p.id} data-testid={`cms-programme-${p.id}`}
                         className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-sm cursor-pointer">
                    <input type="checkbox" checked={!hidden}
                           onChange={() => setForm({
                             ...form,
                             hidden_programme_ids: hidden
                               ? form.hidden_programme_ids.filter(x => x !== p.id)
                               : [...(form.hidden_programme_ids || []), p.id],
                           })} />
                    <span className={hidden ? 'text-slate-400 line-through' : 'text-slate-700'}>
                      {p.name}
                    </span>
                  </label>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Extra programme cards (typed by hand) */}
      <Card data-testid="cms-extra-programmes-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Extra programme cards</CardTitle>
              <CardDescription>For anything not yet set up in Admissions — e.g. grade levels or short courses</CardDescription>
            </div>
            <Button variant="outline" size="sm" data-testid="cms-add-extra-programme-btn"
                    onClick={() => setForm({
                      ...form,
                      extra_programmes: [...(form.extra_programmes || []),
                                         { name: '', award: '', duration: '', description: '' }],
                    })}>
              <Plus className="h-4 w-4 mr-1" /> Add card
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(form.extra_programmes || []).map((p, idx) => (
            <div key={idx} className="border rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3 relative">
              <button onClick={() => setForm({
                        ...form,
                        extra_programmes: form.extra_programmes.filter((_, i) => i !== idx) })}
                      className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                      data-testid={`cms-remove-extra-programme-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </button>
              {[['name', 'Name'], ['award', 'Award'], ['duration', 'Duration']].map(([k, label]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  <Input value={p[k] || ''} data-testid={`cms-extra-programme-${idx}-${k}`}
                         onChange={e => setForm({
                           ...form,
                           extra_programmes: form.extra_programmes.map((x, i) =>
                             i === idx ? { ...x, [k]: e.target.value } : x),
                         })} />
                </div>
              ))}
              <div className="md:col-span-3">
                <Label>Description</Label>
                <Input value={p.description || ''} data-testid={`cms-extra-programme-${idx}-description`}
                       onChange={e => setForm({
                         ...form,
                         extra_programmes: form.extra_programmes.map((x, i) =>
                           i === idx ? { ...x, description: e.target.value } : x),
                       })} />
              </div>
            </div>
          ))}
          {(form.extra_programmes || []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-3">No extra cards</p>
          )}
        </CardContent>
      </Card>

      {/* Testimonials */}
      <Card data-testid="cms-testimonials-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Testimonials</CardTitle>
              <CardDescription>Quotes from parents, students and alumni — written by you</CardDescription>
            </div>
            <Button variant="outline" size="sm" data-testid="cms-add-testimonial-btn"
                    onClick={() => setForm({
                      ...form,
                      testimonials: [...(form.testimonials || []),
                                     { name: '', role: '', quote: '', photo_url: '' }],
                    })}>
              <Plus className="h-4 w-4 mr-1" /> Add quote
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(form.testimonials || []).map((t, idx) => (
            <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
              <button onClick={() => setForm({
                        ...form, testimonials: form.testimonials.filter((_, i) => i !== idx) })}
                      className="absolute top-2 right-2 text-slate-400 hover:text-red-500"
                      data-testid={`cms-remove-testimonial-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[['name', 'Name'], ['role', 'Role (e.g. Parent, Class of 2024)']]
                  .map(([k, label]) => (
                    <div key={k}>
                      <Label>{label}</Label>
                      <Input value={t[k] || ''} data-testid={`cms-testimonial-${idx}-${k}`}
                             onChange={e => setForm({
                               ...form,
                               testimonials: form.testimonials.map((x, i) =>
                                 i === idx ? { ...x, [k]: e.target.value } : x),
                             })} />
                    </div>
                  ))}
                <MediaUpload value={t.photo_url || ''}
                             onChange={(url) => setForm({
                               ...form,
                               testimonials: form.testimonials.map((x, i) =>
                                 i === idx ? { ...x, photo_url: url } : x),
                             })}
                             purpose="avatar" label="Photo"
                             testId={`cms-testimonial-${idx}-photo-upload`}
                             previewClassName="h-16" />
              </div>
              <div>
                <Label>Quote</Label>
                <textarea className="w-full border rounded-lg p-3 text-sm min-h-[70px]"
                          value={t.quote || ''} data-testid={`cms-testimonial-${idx}-quote`}
                          onChange={e => setForm({
                            ...form,
                            testimonials: form.testimonials.map((x, i) =>
                              i === idx ? { ...x, quote: e.target.value } : x),
                          })} />
              </div>
            </div>
          ))}
          {(form.testimonials || []).length === 0 && (
            <p className="text-sm text-slate-400 text-center py-3">No testimonials yet</p>
          )}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}
                className="px-8" data-testid="save-cms-btn">
          {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default SchoolCMS;
